import { P2GBox, P2GLink, P2GText } from '@parcel2go/core-ui';
import { useContext } from 'react';
import { SmartSendContext } from '../../../lib/context/SmartSendContext';
import { UpdateFilterAction } from '../../../lib/context/SmartSendContextModels';
import { Consts, EditEventType } from '../../../lib/em/EmitTypes';
import { useEventEmitter } from '../../../lib/em/Hooks';
import useProviderName from '../../../hooks/use-provider-name';
import { EditType } from '../item/EditType';
import { SettingsCache } from '~/lib/consts/settings-cache';
import { P2GLinkGroup } from '@parcel2go/core-ui';
import {
  ExternalSearch,
  ExternalSearchAwaitingDispatch,
  ExternalSearchInTransit
} from '../../../lib/consts/ListyConsts';
import { TruncateString } from '../../../lib/helpers/StringHelper';
import { RoutingConstants } from '../../../lib/consts/RoutingConstants';
import { SmartSendProvider } from '~/lib/consts/providers';
import { P2GImageMessage } from '~/components/imagemessage';
import { Trans, useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { useNavExperience } from '~/components/nav/nav-experience';

interface props {
  filterKey: string;
  goTo: (route: string) => void;
  /**
   * Server-side search term (the Algolia query), for lists that search the index rather than the
   * items already in memory. Kept separate from the context's SearchTerm on purpose: that one also
   * drives containsSearchTerm, whose strict substring match would re-filter and discard valid
   * Algolia hits.
   */
  searchTerm?: string;
  onClearSearch?: () => void;
}

const ListyNoItems: React.FC<props> = (props) => {
  const providerName = useProviderName();
  const ctx = useContext(SmartSendContext);
  const { emit } = useEventEmitter<EditEventType>();
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const experience = useNavExperience();

  const setAction = (a: EditType) => {
    emit(Consts.Edit, { Id: null, Action: a });
  };

  const resetSearchTerm = () => {
    ctx.dispatch(
      new UpdateFilterAction(props.filterKey, {
        ...ctx.state.filters[props.filterKey].filter,
        CurrentFilter: { ...ctx.state.filters[props.filterKey].filter.CurrentFilter, SearchTerm: '' }
      })
    );
  };

  const searchTerm = props.searchTerm ?? ctx.state.filters[props.filterKey].filter.CurrentFilter.SearchTerm;

  if (searchTerm) {
    return (
      <P2GImageMessage
        image="/theme/smartsend/notifications/no-results.png"
        message={t('NO_RESULT_FALLBACK', { defaultValue: 'No results found' })}
        contentKey="image-smartsend-item-search-no-results"
      >
        <P2GText component="p">
          <Trans
            t={t}
            i18nKey="NO_ITEM_RESULT_ERROR"
            values={{
              searchTerm: TruncateString(searchTerm, 40)
            }}
            defaults="There are no results for <strong>{{searchTerm}}</strong>."
          />
        </P2GText>
        <P2GBox mt={4}>
          <P2GLink onClick={props.onClearSearch ?? resetSearchTerm}>
            {t('CLEAR_SEARCH_LINK', { defaultValue: 'Clear Search' })}
          </P2GLink>
        </P2GBox>
      </P2GImageMessage>
    );
  }

  // Consumer "My Account" experience: omit the SmartSend empty-state CTAs (New Orders, Upload
  // CSV, Add Manual Order) - they are meaningless for non-bulk customers and target SmartSend-only
  // routes. Keep the message tab-aware so the In Transit tab reads correctly (no undelivered
  // parcels) rather than reusing the My Orders "no previous orders" copy.
  if (experience === 'consumer') {
    const isInTransit = props.filterKey === RoutingConstants.InTransit || props.filterKey === ExternalSearchInTransit;
    return (
      <P2GImageMessage
        image={
          isInTransit ? '/theme/smartsend/notifications/in-transit.png' : '/theme/smartsend/notifications/booked.png'
        }
        message={
          isInTransit
            ? t('NO_UNDELIVERED_PARCELS_FALLBACK', { defaultValue: 'You have no undelivered parcels' })
            : t('NO_PREVIOUS_ORDERS_FALLBACK', { defaultValue: 'You have no previous orders' })
        }
        contentKey={isInTransit ? 'image-consumer-in-transit-empty-list' : 'image-consumer-no-orders-empty-list'}
      />
    );
  }

  switch (props.filterKey) {
    case RoutingConstants.InTransit:
    case ExternalSearchInTransit:
      return (
        <P2GImageMessage
          image="/theme/smartsend/notifications/in-transit.png"
          message={t('NO_UNDELIVERED_PARCELS_FALLBACK', { defaultValue: 'You have no undelivered parcels' })}
          contentKey="image-smartsend-in-transit-empty-list"
        >
          <P2GText component="p">
            <Trans
              t={t}
              i18nKey="NO_UNDELIVERED_PARCELS_DESCRIPTION"
              components={{ a: <P2GLink onClick={() => props.goTo(RoutingConstants.OrderHistory)} /> }}
              defaults="Delivered marketplace orders for can be found in <a>Order History</a>."
            />
          </P2GText>
          <P2GText component="p">
            <Trans
              t={t}
              i18nKey="NO_UNDELIVERED_PARCELS_DESCRIPTION_2"
              components={{ a: <P2GLink onClick={() => props.goTo(RoutingConstants.SmartSend)} /> }}
              defaults="New marketplace orders can be found in <a>New Orders</a>."
            />
          </P2GText>
          <P2GBox mt={4}>
            <P2GLinkGroup
              links={[
                SettingsCache.settings.Features.includes('CsvUpload') && (
                  <P2GLink
                    key="csv-upload"
                    onClick={() => {
                      setAction('CSV');
                      props.goTo(RoutingConstants.SmartSend);
                    }}
                  >
                    {t('UPLOAD_CSV_LINK', { defaultValue: 'Upload CSV' })}
                  </P2GLink>
                ),
                <P2GLink
                  key="add-manual-item"
                  onClick={() => {
                    setAction('CREATE');
                    props.goTo(RoutingConstants.SmartSend);
                  }}
                >
                  {t('ADD_MANUAL_ITEM_LINK', { defaultValue: 'Add Manual Order' })}
                </P2GLink>
              ]}
            />
          </P2GBox>
        </P2GImageMessage>
      );
    case RoutingConstants.AwaitingDispatch:
    case ExternalSearchAwaitingDispatch:
      return (
        <P2GImageMessage
          image="/theme/smartsend/notifications/awaiting-dispatch.png"
          message={t('NO_PARCELS_NEED_TRACKING_FALLBACK', {
            defaultValue: 'No parcels need tracking details sending to your marketplace'
          })}
          contentKey="image-smartsend-awaiting-dispatch-empty-list"
        >
          <P2GText component="p">
            <Trans
              t={t}
              i18nKey="NO_PARCELS_NEED_TRACKING_DESCRIPTION"
              components={{ a: <P2GLink onClick={() => props.goTo(RoutingConstants.OrderHistory)} /> }}
              defaults="Marketplace orders you have arranged delivery for can be found in <a>Order History</a>."
            />
          </P2GText>
          <P2GText component="p">
            <Trans
              t={t}
              i18nKey="NO_PARCELS_NEED_TRACKING_DESCRIPTION_2"
              components={{ a: <P2GLink onClick={() => props.goTo(RoutingConstants.SmartSend)} /> }}
              defaults="New marketplace orders can be found in <a>New Orders</a>."
            />
          </P2GText>
          <P2GBox mt={4}>
            <P2GLinkGroup
              links={[
                SettingsCache.settings.Features.includes('CsvUpload') && (
                  <P2GLink
                    key="csv-upload"
                    onClick={() => {
                      setAction('CSV');
                      props.goTo(RoutingConstants.SmartSend);
                    }}
                  >
                    {t('UPLOAD_CSV_LINK', { defaultValue: 'Upload CSV' })}
                  </P2GLink>
                ),
                <P2GLink
                  key="add-manual-item"
                  onClick={() => {
                    setAction('CREATE');
                    props.goTo(RoutingConstants.SmartSend);
                  }}
                >
                  {t('ADD_MANUAL_ITEM_LINK', { defaultValue: 'Add Manual Order' })}
                </P2GLink>
              ]}
            />
          </P2GBox>
        </P2GImageMessage>
      );
    case RoutingConstants.OrderHistory:
    case 'Marketplace Orders':
      return (
        <P2GImageMessage
          image="/theme/smartsend/notifications/booked.png"
          message={t('NO_PREVIOUS_ORDERS_FALLBACK', { defaultValue: 'You have no previous orders' })}
          contentKey="image-smartsend-booked-items-empty-list"
        >
          <P2GText component="p">
            <Trans
              t={t}
              i18nKey="NO_PREVIOUS_ORDERS_DESCRIPTION"
              components={{ a: <P2GLink onClick={() => props.goTo(RoutingConstants.SmartSend)} /> }}
              defaults="New marketplace orders can be found in <a>New Orders</a>."
            />
          </P2GText>
          <P2GBox mt={4}>
            <P2GLinkGroup
              links={[
                SettingsCache.settings.Features.includes('CsvUpload') && (
                  <P2GLink
                    key="csv-upload"
                    onClick={() => {
                      setAction('CSV');
                      props.goTo(RoutingConstants.SmartSend);
                    }}
                  >
                    {t('UPLOAD_CSV_LINK', { defaultValue: 'Upload CSV' })}
                  </P2GLink>
                ),
                <P2GLink
                  key="add-manual-item"
                  onClick={() => {
                    setAction('CREATE');
                    props.goTo(RoutingConstants.SmartSend);
                  }}
                >
                  {t('ADD_MANUAL_ITEM_LINK', { defaultValue: 'Add Manual Order' })}
                </P2GLink>
              ]}
            />
          </P2GBox>
        </P2GImageMessage>
      );
    case ExternalSearch:
      return (
        <P2GImageMessage
          image="/theme/smartsend/notifications/no-smartsend-orders.png"
          message={t('NO_SMARTSEND_ORDERS_FALLBACK', {
            defaultValue: `You have no {{name}} orders`,
            name: providerName(SmartSendProvider)
          })}
          contentKey="image-smartsend-no-orders-empty-list"
        >
          <P2GText component="p">
            <Trans
              t={t}
              i18nKey="NO_SMARTSEND_ORDERS_DESCRIPTION"
              components={{ a: <P2GLink onClick={() => props.goTo(RoutingConstants.SmartSend)} /> }}
              defaults="New marketplace orders can be found in <a>New Orders</a>."
            />
          </P2GText>
          <P2GBox mt={4}>
            <P2GLinkGroup
              links={[
                SettingsCache.settings.Features.includes('CsvUpload') && (
                  <P2GLink
                    key="csv-upload"
                    onClick={() => {
                      setAction('CSV');
                      props.goTo(RoutingConstants.SmartSend);
                    }}
                  >
                    {t('UPLOAD_CSV_LINK', { defaultValue: 'Upload CSV' })}
                  </P2GLink>
                ),
                <P2GLink
                  key="add-manual-item"
                  onClick={() => {
                    setAction('CREATE');
                    props.goTo(RoutingConstants.SmartSend);
                  }}
                >
                  {t('ADD_MANUAL_ITEM_LINK', { defaultValue: 'Add Manual Order' })}
                </P2GLink>
              ]}
            />
          </P2GBox>
        </P2GImageMessage>
      );
    default:
      return (
        <P2GImageMessage
          image="/theme/common/notifications/no-results.png"
          contentKey="image-smartsend-items-empty-list"
        />
      );
  }
};

export default ListyNoItems;
